<template>
  <div class="card w-96 bg-base-100 card-bordered">
    <div class="card-body">
      <h2 class="card-title justify-center mb-4">Register</h2>
      
      <form @submit.prevent="handleRegister" class="space-y-4">
        <div class="form-control">
          <label class="label">
            <span class="label-text">Full Name</span>
          </label>
          <input
            v-model="form.full_name"
            type="text"
            placeholder="John Doe"
            class="input input-bordered"
            :class="{ 'input-error': errors.full_name }"
            required
          />
          <label v-if="errors.full_name" class="label">
            <span class="label-text-alt text-error">{{ errors.full_name }}</span>
          </label>
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text">Username</span>
          </label>
          <input
            v-model="form.username"
            type="text"
            placeholder="johndoe"
            class="input input-bordered"
            :class="{ 'input-error': errors.username }"
            required
          />
          <label v-if="errors.username" class="label">
            <span class="label-text-alt text-error">{{ errors.username }}</span>
          </label>
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text">Email</span>
          </label>
          <input
            v-model="form.email"
            type="email"
            placeholder="your@email.com"
            class="input input-bordered"
            :class="{ 'input-error': errors.email }"
            required
          />
          <label v-if="errors.email" class="label">
            <span class="label-text-alt text-error">{{ errors.email }}</span>
          </label>
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text">Password</span>
          </label>
          <input
            v-model="form.password"
            type="password"
            placeholder="Enter your password"
            class="input input-bordered"
            :class="{ 'input-error': errors.password }"
            required
          />
          <label v-if="errors.password" class="label">
            <span class="label-text-alt text-error">{{ errors.password }}</span>
          </label>
        </div>

        <div class="form-control">
          <label class="label">
            <span class="label-text">Confirm Password</span>
          </label>
          <input
            v-model="form.confirmPassword"
            type="password"
            placeholder="Confirm your password"
            class="input input-bordered"
            :class="{ 'input-error': errors.confirmPassword }"
            required
          />
          <label v-if="errors.confirmPassword" class="label">
            <span class="label-text-alt text-error">{{ errors.confirmPassword }}</span>
          </label>
        </div>

        <div v-if="error" class="alert alert-error">
          <span>{{ error }}</span>
        </div>

        <div class="form-control mt-6">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading"
          >
            <span v-if="loading" class="loading loading-spinner loading-sm"></span>
            {{ loading ? 'Creating account...' : 'Register' }}
          </button>
        </div>
      </form>

      <div class="divider">OR</div>
      
      <div class="text-center">
        <p class="text-sm text-gray-600">
          Already have an account?
          <button @click="$emit('switch-to-login')" class="link link-primary">
            Login here
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useAuthStore } from '../stores/auth'

export default {
  name: 'RegisterForm',
  emits: ['switch-to-login'],
  setup() {
    const authStore = useAuthStore()
    
    const form = reactive({
      full_name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    })
    
    const errors = reactive({
      full_name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    })
    
    const error = ref('')
    const loading = ref(false)

    const validateForm = () => {
      errors.full_name = ''
      errors.username = ''
      errors.email = ''
      errors.password = ''
      errors.confirmPassword = ''
      
      if (!form.full_name.trim()) {
        errors.full_name = 'Full name is required'
      }
      
      if (!form.username.trim()) {
        errors.username = 'Username is required'
      } else if (form.username.length < 3) {
        errors.username = 'Username must be at least 3 characters'
      } else if (!/^[a-zA-Z0-9_]+$/.test(form.username)) {
        errors.username = 'Username can only contain letters, numbers, and underscores'
      }
      
      if (!form.email) {
        errors.email = 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errors.email = 'Please enter a valid email'
      }
      
      if (!form.password) {
        errors.password = 'Password is required'
      } else if (form.password.length < 8) {
        errors.password = 'Password must be at least 8 characters'
      }
      
      if (!form.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password'
      } else if (form.password !== form.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match'
      }
      
      return !Object.values(errors).some(error => error)
    }

    const handleRegister = async () => {
      if (!validateForm()) return
      
      loading.value = true
      error.value = ''
      
      try {
        await authStore.register({
          full_name: form.full_name,
          username: form.username,
          email: form.email,
          password: form.password
        })
      } catch (err) {
        error.value = err.message || 'Registration failed. Please try again.'
      } finally {
        loading.value = false
      }
    }

    return {
      form,
      errors,
      error,
      loading,
      handleRegister
    }
  }
}
</script> 